import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { SimpleGrid, type SimpleGridCols } from '../../core/SimpleGrid/SimpleGrid.js';
import { Card } from '../../core/Card/Card.js';

export interface EntityCardGridProps<R> extends Omit<BaseProps, 'children'> {
  records: R[];
  /** Stable ID: accessor key or function. Defaults to index. */
  idAccessor?: keyof R | ((row: R, i: number) => string | number);
  /** Renders each row as a card body. The outer `<Card>` is provided. */
  renderCard: (row: R, index: number) => ReactNode;
  /** Number of columns at the widest breakpoint. Default 3. */
  /**
   * A fixed count or the responsive map `SimpleGrid` accepts, for example
   * `{ base: 1, sm: 2, lg: 3 }`. Typed as a bare number before, which held
   * every card view at its desktop column count down to a phone.
   */
  cols?: SimpleGridCols;
  /** Optional click handler: turns each card into a button-like surface. */
  onCardClick?: (row: R, index: number) => void;
  /** Gap between cards. Default 'md'. */
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Card-view alternative to a `DataTable` - same `records` array, but each row
 * is a `<Card>` whose body is yours to compose. Pair with `<TableShell>`'s
 * `view` switcher.
 *
 *   <EntityCardGrid
 *     records={tenants}
 *     idAccessor="id"
 *     onCardClick={(t) => navigate(`/tenants/${t.id}`)}
 *     renderCard={(t) => (
 *       <Stack gap="xs">
 *         <Title order={5}>{t.name}</Title>
 *         <Text fz="sm">{t.country_iso}</Text>
 *       </Stack>
 *     )}
 *   />
 */
export const EntityCardGrid = forwardRef(function EntityCardGrid<R>(
  { records, idAccessor, renderCard, cols = 3, onCardClick, spacing = 'md', ...rest }: EntityCardGridProps<R>,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const keyFor = (row: R, i: number): string | number => {
    if (typeof idAccessor === 'function') return idAccessor(row, i);
    if (idAccessor && (row as Record<string, unknown>)[idAccessor as string] != null) {
      return (row as Record<string, unknown>)[idAccessor as string] as string | number;
    }
    return i;
  };

  return (
    <SimpleGrid ref={ref} cols={cols} spacing={spacing} {...rest}>
      {records.map((row, i) => {
        const onClick = onCardClick ? () => onCardClick(row, i) : undefined;
        const card = <Card withBorder>{renderCard(row, i)}</Card>;
        if (!onClick) return <div key={keyFor(row, i)}>{card}</div>;
        return (
          <div
            key={keyFor(row, i)}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {card}
          </div>
        );
      })}
    </SimpleGrid>
  );
}) as <R>(props: EntityCardGridProps<R> & { ref?: Ref<HTMLDivElement> }) => ReactNode;
